import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Typography,
  Button,
  Box,
  Container,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Pagination,
  Select,
  Menu,
  FormControl,
  MenuItem,
  MenuItem as SelectMenuItem,
  InputLabel,
  TextField,
  InputAdornment,
  IconButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Stack,
  Link,
} from '@mui/material';
import {
  Refresh,
  Face,
  Delete,
  Visibility,
  PersonAdd,
  Search,
  FilterList,
  ExpandMore,
  ExpandLess,
  Edit,
  Restore,
  CalendarMonth,
  Videocam,
  VolumeUp,
  Login,
  Logout,
  Mic,
  Stop,
  CloudUpload,
  SmartToy,
  Close,
  SwapHoriz,
  Tune,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getIntrusionPersons, getIntrusionAccounts, softDeleteFace, getFaceImage, updateFaceImage, restoreFace, uploadEmployeeVoice, getStoredToken, listAIVoiceApiKeys, getDefaultAIVoiceKeySelection, saveAIVoiceApiKey, generateEmployeeAIVoice } from '../utils/api';
import PageHeader from './PageHeader';
import ClearAllFiltersButton from './ClearAllFiltersButton';
import {
  pageFiltersActionsSx,
  pageFiltersControlsSx,
  pageFiltersStackSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import SimilarityThresholdDialog from './SimilarityThresholdDialog';
import PersonProfileSwitchDialog from './PersonProfileSwitchDialog';
import { formatDate } from '../utils/dateUtils';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import toWav from 'audiobuffer-to-wav';
import { baseURL } from '../config/api';

const NAME_SEARCH_DEBOUNCE_MS = 400;

const IntrusionProfiles = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [persons, setPersons] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
    has_next: false,
    has_prev: false,
    total_pages: 0,
    current_page: 1,
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [imageDialog, setImageDialog] = useState({ open: false, imageUrl: null, faceName: '' });
  const [editDialog, setEditDialog] = useState({ open: false, face: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [profileSwitchDialog, setProfileSwitchDialog] = useState({ open: false, person: null });
  const [similarityThresholdDialog, setSimilarityThresholdDialog] = useState({
    open: false,
    person: null,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [query, setQuery] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [rowExpanded, setRowExpanded] = useState({});
  const [enrolledVoiceMenu, setEnrolledVoiceMenu] = useState({ anchorEl: null, person: null });
  const [enrolledManageMenu, setEnrolledManageMenu] = useState({ anchorEl: null, person: null });
  const [showDeleted, setShowDeleted] = useState(false); // Show active by default
  // Voice Upload Modal state
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [voiceModalEmployee, setVoiceModalEmployee] = useState(null); // { employeeId, employeeName, personId, mode }
  const [checkinVoiceBlob, setCheckinVoiceBlob] = useState(null);
  const [checkoutVoiceBlob, setCheckoutVoiceBlob] = useState(null);
  const [isRecordingCheckin, setIsRecordingCheckin] = useState(false);
  const [isRecordingCheckout, setIsRecordingCheckout] = useState(false);
  const [voiceUploading, setVoiceUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [checkinAudioUrl, setCheckinAudioUrl] = useState(null);
  const [checkoutAudioUrl, setCheckoutAudioUrl] = useState(null);
  const [playingVoice, setPlayingVoice] = useState(null); // Track currently playing voice: { employeeId, voiceType } or null
  // AI Voice state
  const [aiVoiceDialogOpen, setAiVoiceDialogOpen] = useState(false);
  const [aiVoiceType, setAiVoiceType] = useState('checkin');
  const [aiVoiceApiKeyInput, setAiVoiceApiKeyInput] = useState('');
  const [aiVoiceSavedKeys, setAiVoiceSavedKeys] = useState([]);
  const [aiVoiceSelectedKeyId, setAiVoiceSelectedKeyId] = useState('');
  const [aiVoiceText, setAiVoiceText] = useState('');
  const [aiVoiceLoadingKeys, setAiVoiceLoadingKeys] = useState(false);
  const [aiVoiceGenerating, setAiVoiceGenerating] = useState(false);
  const isLoadingRef = useRef(false);
  const lastCallParamsRef = useRef(null);
  const isLoadingAccountsRef = useRef(false);

  const loadAccounts = async () => {
    // Prevent duplicate concurrent calls
    if (isLoadingAccountsRef.current) {
      return;
    }
    
    // If accounts are already loaded, don't reload
    if (accounts.length > 0) {
      return;
    }
    
    isLoadingAccountsRef.current = true;

    try {
      const response = await getIntrusionAccounts(100, 0, true);
      setAccounts(response.companies || []);
    } catch (error) {
      console.error('Error loading intrusion accounts:', error);
    } finally {
      isLoadingAccountsRef.current = false;
    }
  };

  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPersons = async () => {
    // Create a unique key for this call
    const callKey = `${pagination.current_page}-${pagination.limit}-${accountFilter}-${query}-${showDeleted}`;
    
    // Prevent duplicate concurrent calls with same parameters
    if (isLoadingRef.current && lastCallParamsRef.current === callKey) {
      return;
    }
    
    isLoadingRef.current = true;
    lastCallParamsRef.current = callKey;

    setLoading(true);
    try {
      const offset = (pagination.current_page - 1) * pagination.limit;
      const response = await getIntrusionPersons(
        pagination.limit,
        offset,
        showDeleted, // Use showDeleted state
        query || null,
        null,
        accountFilter || null
      );
      setPersons(response.persons || []);
      setPagination(prevPagination => ({
        ...prevPagination,
        total: response.pagination?.total || 0,
        has_next: response.pagination?.has_next || false,
        has_prev: response.pagination?.has_prev || false,
        total_pages: response.pagination?.total_pages || 0,
        current_page: prevPagination.current_page,
        limit: prevPagination.limit,
        offset: prevPagination.offset,
      }));
    } catch (error) {
      console.error('Error loading intrusion profiles:', error);
      showSnackbar('Failed to load intrusion profiles', 'error');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  useEffect(() => {
    loadPersons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, pagination.limit, accountFilter, query, showDeleted]);

  const handleSimilarityThresholdSaved = useCallback((personId, threshold) => {
    setPersons((prev) =>
      prev.map((p) =>
        String(p?.person_id || '') === String(personId)
          ? { ...p, similarity_threshold: threshold }
          : p
      )
    );
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeEnrolledVoiceMenu = () => {
    setEnrolledVoiceMenu({ anchorEl: null, person: null });
  };

  const closeEnrolledManageMenu = () => {
    setEnrolledManageMenu({ anchorEl: null, person: null });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const resolveUpdatedByName = useCallback((person) => {
    const rawValue =
      person?.updated_by_name ??
      person?.updated_by ??
      person?.last_updated_by ??
      person?.updatedBy ??
      person?.updated_by_user?.name ??
      person?.updated_by_user?.full_name ??
      person?.updated_by_user?.username;

    if (!rawValue) return '-';
    if (typeof rawValue === 'string') return rawValue.trim() || '-';
    if (typeof rawValue === 'object') {
      const nestedValue = rawValue?.name ?? rawValue?.full_name ?? rawValue?.username;
      if (!nestedValue) return '-';
      return String(nestedValue).trim() || '-';
    }
    return String(rawValue);
  }, []);

  const resolveUpdatedAt = useCallback((person) => {
    const rawValue =
      person?.updated_at ??
      person?.last_updated ??
      person?.updatedAt ??
      person?.updated_on ??
      person?.modified_at ??
      person?.modified_on;
    const formatted = formatDate(rawValue);
    return formatted && formatted !== '-' ? formatted : '-';
  }, []);

  const renderUpdatedByCell = useCallback(
    (person) => {
      const name = resolveUpdatedByName(person);
      const time = resolveUpdatedAt(person);
      if (name === '-' && time === '-') {
        return <span style={{ color: '#999' }}>—</span>;
      }
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {time}
          </Typography>
        </Box>
      );
    },
    [resolveUpdatedAt, resolveUpdatedByName]
  );

  const handlePageChange = (event, value) => {
    setPagination({ ...pagination, current_page: value });
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    setPagination(prev => ({
      ...prev,
      limit: newLimit,
      current_page: 1,
      offset: 0,
    }));
    // The useEffect will automatically reload when pagination.limit changes
  };

  const applyQueryFilterNow = useCallback((rawValue) => {
    const nextQuery = (rawValue || '').trimStart();
    setQueryInput(nextQuery);
    setQuery(nextQuery);
    setPagination((prev) => (
      prev.current_page === 1 && prev.offset === 0
        ? prev
        : { ...prev, current_page: 1, offset: 0 }
    ));
  }, []);

  const hasActiveFilters = useMemo(() => (
    !!(query?.trim()) || !!accountFilter || showDeleted
  ), [accountFilter, query, showDeleted]);

  const handleClearAllFilters = useCallback(() => {
    setQuery('');
    setQueryInput('');
    setAccountFilter('');
    setShowDeleted(false);
    setPagination((prev) => ({ ...prev, current_page: 1, offset: 0 }));
  }, []);

  useEffect(() => {
    if (queryInput === query) return;
    const timeoutId = setTimeout(() => {
      setQuery(queryInput);
      setPagination((prev) => (
        prev.current_page === 1 && prev.offset === 0
          ? prev
          : { ...prev, current_page: 1, offset: 0 }
      ));
    }, NAME_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [queryInput, query]);

  const handleViewFaceImage = async (face) => {
    try {
      const imageBlob = await getFaceImage(face.face_id);
      const imageUrl = URL.createObjectURL(imageBlob);
      setImageDialog({ open: true, imageUrl, faceName: face.name || 'Unknown' });
    } catch (error) {
      console.error('Error loading face image:', error);
      showSnackbar('Failed to load image', 'error');
    }
  };

  const handleCloseImageDialog = () => {
    if (imageDialog.imageUrl) {
      URL.revokeObjectURL(imageDialog.imageUrl);
    }
    setImageDialog({ open: false, imageUrl: null, faceName: '' });
  };

  const handleOpenEdit = (face) => {
    setEditDialog({ open: true, face });
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleCloseEdit = () => {
    setEditDialog({ open: false, face: null });
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    // Stop camera if running
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
  };

  const onFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: false
      });
      streamRef.current = stream;
      setIsCameraOn(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      showSnackbar('Failed to access camera', 'error');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
  };

  const toggleCamera = async () => {
    if (streamRef.current) {
      stopCamera();
    } else {
      await startCamera();
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
      setSelectedFile(file);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      // Stop camera after capture
      stopCamera();
    }, 'image/jpeg', 0.92);
  };

  const submitEdit = async () => {
    if (!editDialog.face || !selectedFile) {
      showSnackbar('Please select or capture an image', 'warning');
      return;
    }
    try {
      await updateFaceImage(editDialog.face.face_id, selectedFile);
      showSnackbar('Image updated successfully', 'success');
      handleCloseEdit();
      loadPersons();
    } catch (err) {
      console.error('Update failed:', err);
      showSnackbar(err.message || 'Failed to update image', 'error');
    }
  };

  const handleSoftDeleteFace = (face) => {
    setConfirmDialog({
      open: true,
      title: 'Soft Delete Face',
      message: `Are you sure you want to soft delete the face for ${face.name}? This can be restored later.`,
      onConfirm: () => performSoftDelete(face.face_id),
    });
  };

  const performSoftDelete = async (faceId) => {
    try {
      const response = await softDeleteFace(faceId);
      if (response.ok) {
        showSnackbar('Face soft deleted successfully', 'success');
        loadPersons();
      } else {
        showSnackbar('Failed to soft delete face', 'error');
      }
    } catch (error) {
      console.error('Error soft deleting face:', error);
      showSnackbar('Failed to soft delete face', 'error');
    }
  };

  const handleRestoreFace = (face) => {
    setConfirmDialog({
      open: true,
      title: 'Activate Face',
      message: `Are you sure you want to activate the face for ${face.name}? This will make it active again.`,
      onConfirm: () => performRestore(face.face_id),
    });
  };

  const performRestore = async (faceId) => {
    try {
      const response = await restoreFace(faceId);
      if (response) {
        showSnackbar('Face restored successfully', 'success');
        loadPersons();
      } else {
        showSnackbar('Failed to restore face', 'error');
      }
    } catch (error) {
      console.error('Error restoring face:', error);
      showSnackbar('Failed to restore face', 'error');
    }
  };

  const handleDeleteAllFaces = (person) => {
    const activeFaces = person.faces?.filter(face => !face.deleted_at) || [];
    if (activeFaces.length === 0) {
      showSnackbar('No active faces to delete', 'warning');
      return;
    }
    setConfirmDialog({
      open: true,
      title: 'Delete All Faces',
      message: `Are you sure you want to delete all ${activeFaces.length} face(s) for ${person.name}? This can be restored later.`,
      onConfirm: () => performDeleteAllFaces(activeFaces),
    });
  };

  const performDeleteAllFaces = async (faces) => {
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const face of faces) {
        try {
          const response = await softDeleteFace(face.face_id);
          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`Error deleting face ${face.face_id}:`, error);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        showSnackbar(`Successfully deleted ${successCount} face(s)`, 'success');
        loadPersons();
      }
      if (failCount > 0) {
        showSnackbar(`Failed to delete ${failCount} face(s)`, 'error');
      }
    } catch (error) {
      console.error('Error deleting faces:', error);
      showSnackbar('Failed to delete faces', 'error');
    }
  };

  const handleRestoreAllFaces = (person) => {
    const deletedFaces = person.faces?.filter(face => face.deleted_at) || [];
    if (deletedFaces.length === 0) {
      showSnackbar('No deleted faces to activate', 'warning');
      return;
    }
    setConfirmDialog({
      open: true,
      title: 'Activate All Faces',
      message: `Are you sure you want to activate all ${deletedFaces.length} face(s) for ${person.name}? This will make them active again.`,
      onConfirm: () => performRestoreAllFaces(deletedFaces),
    });
  };

  const performRestoreAllFaces = async (faces) => {
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const face of faces) {
        try {
          const response = await restoreFace(face.face_id);
          if (response) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`Error restoring face ${face.face_id}:`, error);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        showSnackbar(`Successfully restored ${successCount} face(s)`, 'success');
        loadPersons();
      }
      if (failCount > 0) {
        showSnackbar(`Failed to restore ${failCount} face(s)`, 'error');
      }
    } catch (error) {
      console.error('Error restoring faces:', error);
      showSnackbar('Failed to restore faces', 'error');
    }
  };

  // Voice functions
  const openVoiceModal = (employeeId, employeeName, personId = null, mode = 'add') => {
    setVoiceModalEmployee({ employeeId, employeeName, personId, mode });
    setCheckinVoiceBlob(null);
    setCheckoutVoiceBlob(null);
    setIsRecordingCheckin(false);
    setIsRecordingCheckout(false);
    setVoiceModalOpen(true);
  };

  const closeVoiceModal = () => {
    // Stop any active recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setVoiceModalOpen(false);
    setVoiceModalEmployee(null);
    setCheckinVoiceBlob(null);
    setCheckoutVoiceBlob(null);
    setIsRecordingCheckin(false);
    setIsRecordingCheckout(false);
  };

  const handleFileUpload = (e, voiceType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'audio/wav' && !file.name.toLowerCase().endsWith('.wav')) {
      showSnackbar('Please upload a WAV file', 'warning');
      return;
    }
    
    if (voiceType === 'checkin') {
      setCheckinVoiceBlob(file);
    } else {
      setCheckoutVoiceBlob(file);
    }
    showSnackbar(`${voiceType === 'checkin' ? 'IN' : 'OUT'} voice file selected`, 'success');
  };

  const startRecording = async (voiceType) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Let browser choose supported mimeType (Safari doesn't support webm)
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Create blob from recorded chunks
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        
        try {
          const wavBlob = await convertToWav(audioBlob);
          if (voiceType === 'checkin') {
            setCheckinVoiceBlob(wavBlob);
            setIsRecordingCheckin(false);
          } else {
            setCheckoutVoiceBlob(wavBlob);
            setIsRecordingCheckout(false);
          }
          showSnackbar(`${voiceType === 'checkin' ? 'IN' : 'OUT'} voice recorded successfully`, 'success');
        } catch (err) {
          console.error('Error converting to WAV:', err);
          showSnackbar('Failed to process recording', 'error');
          if (voiceType === 'checkin') {
            setIsRecordingCheckin(false);
          } else {
            setIsRecordingCheckout(false);
          }
        }
      };

      // Start recording with timeslice for better performance
      mediaRecorder.start(100);
      if (voiceType === 'checkin') {
        setIsRecordingCheckin(true);
      } else {
        setIsRecordingCheckout(true);
      }
    } catch (err) {
      console.error('Error accessing microphone:', err);
      showSnackbar('Could not access microphone. Please allow microphone permission.', 'error');
    }
  };

  const stopRecording = (voiceType) => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  // Convert audio blob to wav blob using audiobuffer-to-wav library
  const convertToWav = async (audioBlob) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    // Use the optimized library for WAV conversion
    const wavBuffer = toWav(audioBuffer);
    audioContext.close(); // Clean up
    return new Blob([wavBuffer], { type: 'audio/wav' });
  };

  // Create audio URLs when blobs change
  useEffect(() => {
    if (checkinVoiceBlob) {
      const url = URL.createObjectURL(checkinVoiceBlob);
      setCheckinAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setCheckinAudioUrl(null);
    }
  }, [checkinVoiceBlob]);

  useEffect(() => {
    if (checkoutVoiceBlob) {
      const url = URL.createObjectURL(checkoutVoiceBlob);
      setCheckoutAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setCheckoutAudioUrl(null);
    }
  }, [checkoutVoiceBlob]);

  const saveVoices = async () => {
    if (!voiceModalEmployee) return;
    
    const { employeeId, personId } = voiceModalEmployee;
    const targetId = personId || employeeId;
    
    if (!checkinVoiceBlob && !checkoutVoiceBlob) {
      showSnackbar('Please upload or record at least one voice', 'warning');
      return;
    }
    
    setVoiceUploading(true);
    try {
      let successCount = 0;
      
      if (checkinVoiceBlob) {
        await uploadEmployeeVoice(targetId, 'checkin', checkinVoiceBlob);
        successCount++;
      }
      
      if (checkoutVoiceBlob) {
        await uploadEmployeeVoice(targetId, 'checkout', checkoutVoiceBlob);
        successCount++;
      }
      
      showSnackbar(`${successCount} voice(s) uploaded successfully`, 'success');
      closeVoiceModal();
      loadPersons();
    } catch (err) {
      console.error('Error uploading voice:', err);
      showSnackbar(`Failed to upload voice: ${err.message}`, 'error');
    } finally {
      setVoiceUploading(false);
    }
  };

  const playVoice = async (employeeId, voiceType) => {
    // Prevent multiple simultaneous playback
    if (playingVoice) {
      showSnackbar('Please wait for the current voice to finish playing', 'warning');
      return;
    }
   
    try {
      console.log(`Playing ${voiceType} voice for employee ${employeeId}...`);
      
      // Set playing state immediately
      setPlayingVoice({ employeeId, voiceType });
      
      const token = getStoredToken();
      console.log('Token available:', !!token);
      
      if (!token) {
        setPlayingVoice(null);
        throw new Error('Authentication required - please login again');
      }
      
      // Fetch voice file from authenticated API endpoint using configured base URL
      const response = await fetch(`${baseURL}/api/v1/voice/${employeeId}/${voiceType}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'audio/wav'
        }
      });
      
      console.log('Response status:', response.status, 'Content-Type:', response.headers.get('content-type'));
      
      if (!response.ok) {
        setPlayingVoice(null);
        if (response.status === 404) {
          throw new Error(`${voiceType === 'checkin' ? 'IN' : 'OUT'} voice not found`);
        }
        throw new Error(`Failed to load voice: ${response.statusText}`);
      }
      
      const audioBlob = await response.blob();
      console.log('Audio blob received, size:', audioBlob.size, 'type:', audioBlob.type);
      
      if (audioBlob.size === 0) {
        setPlayingVoice(null);
        throw new Error('Empty audio file received');
      }
      
      // Create object URL and play
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        setPlayingVoice(null);
      };
      
      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        URL.revokeObjectURL(audioUrl);
        setPlayingVoice(null);
        showSnackbar('Failed to play voice', 'error');
      };
      
      await audio.play();
      console.log('Voice playback started');
      
    } catch (error) {
      console.error('Error playing voice:', error);
      setPlayingVoice(null);
      showSnackbar(error.message || 'Failed to play voice', 'error');
    }
  };

  // AI Voice functions
  const loadSavedAIVoiceKeys = async (autoSelectDefault = false) => {
    setAiVoiceLoadingKeys(true);
    try {
      const res = await listAIVoiceApiKeys();
      const keys = Array.isArray(res?.data) ? res.data : [];
      setAiVoiceSavedKeys(keys);
      if (autoSelectDefault) {
        const defaultKey = getDefaultAIVoiceKeySelection(keys);
        if (defaultKey) {
          setAiVoiceSelectedKeyId(defaultKey.credentialId);
          setAiVoiceApiKeyInput(defaultKey.hint);
        }
      }
      return keys;
    } catch (err) {
      console.error('Failed to load saved AI voice keys:', err);
      setAiVoiceSavedKeys([]);
      return [];
    } finally {
      setAiVoiceLoadingKeys(false);
    }
  };

  const openAIVoiceDialog = async (voiceType) => {
    if (!voiceModalEmployee) return;
    const employeeName = voiceModalEmployee?.employeeName || 'Employee';
    const defaultText = voiceType === 'checkout' ? `Goodbye ${employeeName}` : `Welcome ${employeeName}`;
    setAiVoiceType(voiceType);
    setAiVoiceText(defaultText);
    setAiVoiceApiKeyInput('');
    setAiVoiceSelectedKeyId('');
    setAiVoiceDialogOpen(true);
    await loadSavedAIVoiceKeys(true);
  };

  const closeAIVoiceDialog = () => {
    setAiVoiceDialogOpen(false);
    setAiVoiceApiKeyInput('');
    setAiVoiceSelectedKeyId('');
    setAiVoiceText('');
  };

  const base64ToWavBlob = (base64Audio) => {
    const byteChars = window.atob(base64Audio);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i += 1) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: 'audio/wav' });
  };

  const handleGenerateAIVoice = async () => {
    if (!voiceModalEmployee) return;
    const { employeeId, personId } = voiceModalEmployee;
    const targetId = personId || employeeId;
    const trimmedKey = aiVoiceApiKeyInput.trim();
    const trimmedText = aiVoiceText.trim();

    if (!aiVoiceSelectedKeyId && !trimmedKey) {
      showSnackbar('Please select a saved key or enter a new API key', 'warning');
      return;
    }

    setAiVoiceGenerating(true);
    try {
      let payload = {};
      if (aiVoiceSelectedKeyId) {
        payload = {
          credential_id: aiVoiceSelectedKeyId,
          provider: 'cartesia',
          credential_type: 'voice_generation',
          text: trimmedText,
        };
      } else {
        const saveRes = await saveAIVoiceApiKey(trimmedKey);
        const savedCredentialId = saveRes?.data?.credential_id;
        if (savedCredentialId) {
          payload = {
            credential_id: savedCredentialId,
            provider: 'cartesia',
            credential_type: 'voice_generation',
            text: trimmedText,
          };
        } else {
          payload = {
            api_key: trimmedKey,
            provider: 'cartesia',
            credential_type: 'voice_generation',
            text: trimmedText,
          };
        }
      }

      payload.save_to_db = false;
      const res = await generateEmployeeAIVoice(targetId, aiVoiceType, payload);
      const audioBase64 = res?.audio_base64;
      if (!audioBase64) {
        throw new Error('Generated voice preview not received');
      }
      const wavBlob = base64ToWavBlob(audioBase64);
      if (aiVoiceType === 'checkin') {
        setCheckinVoiceBlob(wavBlob);
      } else {
        setCheckoutVoiceBlob(wavBlob);
      }
      showSnackbar(`${aiVoiceType === 'checkin' ? 'IN' : 'OUT'} voice generated. Click Save Voices to persist.`, 'success');
      closeAIVoiceDialog();
      loadPersons();
    } catch (err) {
      console.error('AI voice generation failed:', err);
      showSnackbar(`Failed to generate AI voice: ${err.message}`, 'error');
    } finally {
      setAiVoiceGenerating(false);
    }
  };

  const toggleRowExpanded = (personId) => {
    setRowExpanded(prev => ({ ...prev, [personId]: !prev[personId] }));
  };

  const handleCreateProfile = () => {
    navigate('/intrusion-profile/register-intrusion-profile');
  };

  const formatAngleLabel = (angle, metadata = {}) => {
    if (metadata && metadata.custom_angle_label) return metadata.custom_angle_label;
    if (!angle) return '';
    const s = String(angle).toLowerCase();
    if (s === 'left_profile') return 'left';
    if (s === 'right_profile') return 'right';
    if (s === 'custom_angle') return 'custom';
    return s.replace('_', ' ');
  };

  const getQualityColor = (quality) => {
    if (quality >= 0.9) return '#138636';
    if (quality >= 0.7) return '#FFBC11';
    return '#C91432';
  };

  const getMatchingFaces = useCallback((person) => {
    const faces = Array.isArray(person?.faces) ? person.faces : [];
    return faces.filter((face) => (showDeleted ? face?.deleted_at : !face?.deleted_at));
  }, [showDeleted]);

  const visiblePersons = persons.filter((person) => getMatchingFaces(person).length > 0);

  const renderEnrolledAngles = (person) => {
    const expanded = !!rowExpanded[person.person_id];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={expanded ? 'Hide angle details' : 'View angle details'}>
          <Button
            size="small"
            variant="outlined"
            endIcon={expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
            onClick={() => toggleRowExpanded(person.person_id)}
            sx={{
              textTransform: 'none',
              borderColor: primary,
              color: primary,
              '&:hover': { borderColor: primary, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
            }}
          >
            View
          </Button>
        </Tooltip>
      </Box>
    );
  };

  const renderEnrolledVoice = (person) => {
    const menuOpen = Boolean(
      enrolledVoiceMenu.anchorEl &&
      enrolledVoiceMenu.person &&
      enrolledVoiceMenu.person.person_id === person.person_id
    );
    const hasIn = !!(person.checkin_voice_path && person.checkin_voice_path !== '');
    const hasOut = !!(person.checkout_voice_path && person.checkout_voice_path !== '');
    const playDisabled = !!playingVoice || showDeleted;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          endIcon={menuOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          onClick={(e) =>
            setEnrolledVoiceMenu((prev) =>
              prev.anchorEl && prev.person?.person_id === person.person_id
                ? { anchorEl: null, person: null }
                : { anchorEl: e.currentTarget, person }
            )
          }
          sx={{
            textTransform: 'none',
            borderColor: primary,
            color: primary,
            '&:hover': { borderColor: primary, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
          }}
        >
          Voice
        </Button>
        <Menu
          anchorEl={enrolledVoiceMenu.anchorEl}
          open={menuOpen}
          onClose={closeEnrolledVoiceMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 200 } }}
        >
          <MenuItem
            disabled={!hasIn || playDisabled}
            onClick={() => {
              if (!hasIn || playDisabled) return;
              closeEnrolledVoiceMenu();
              playVoice(person.person_id, 'checkin');
            }}
          >
            <ListItemIcon>
              <VolumeUp fontSize="small" sx={{ color: hasIn && !playDisabled ? 'primary.main' : undefined }} />
            </ListItemIcon>
            <ListItemText primary="Play IN" />
          </MenuItem>
          <MenuItem
            disabled={!hasOut || playDisabled}
            onClick={() => {
              if (!hasOut || playDisabled) return;
              closeEnrolledVoiceMenu();
              playVoice(person.person_id, 'checkout');
            }}
          >
            <ListItemIcon>
              <VolumeUp fontSize="small" sx={{ color: hasOut && !playDisabled ? 'secondary.main' : undefined }} />
            </ListItemIcon>
            <ListItemText primary="Play OUT" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledVoiceMenu();
              if (hasIn && hasOut) {
                openVoiceModal(person.person_id, person.name, person.person_id, 'update');
              } else {
                openVoiceModal(person.person_id, person.name, person.person_id);
              }
            }}
          >
            <ListItemIcon>
              <Refresh fontSize="small" sx={{ color: showDeleted ? undefined : 'primary.main' }} />
            </ListItemIcon>
            <ListItemText primary="Update voice" />
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  const renderEnrolledActions = (person) => {
    const menuOpen = Boolean(
      enrolledManageMenu.anchorEl &&
      enrolledManageMenu.person &&
      enrolledManageMenu.person.person_id === person.person_id
    );
    const logsDisabled =
      !person.faces || person.faces.length === 0 || person.faces.every((face) => face.deleted_at);
    const showUnavailableAction = (label) => {
      showSnackbar(`${label} is not available for intrusion profiles yet`, 'info');
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          endIcon={menuOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          onClick={(e) =>
            setEnrolledManageMenu((prev) =>
              prev.anchorEl && prev.person?.person_id === person.person_id
                ? { anchorEl: null, person: null }
                : { anchorEl: e.currentTarget, person }
            )
          }
          sx={{
            textTransform: 'none',
            borderColor: primary,
            color: primary,
            '&:hover': { borderColor: primary, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
          }}
        >
          Manage
        </Button>
        <Menu
          anchorEl={enrolledManageMenu.anchorEl}
          open={menuOpen}
          onClose={closeEnrolledManageMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 220 } }}
        >
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              showUnavailableAction('Edit details');
            }}
          >
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit details" />
          </MenuItem>
          <MenuItem
            disabled={logsDisabled || showDeleted}
            onClick={() => {
              if (logsDisabled || showDeleted) return;
              closeEnrolledManageMenu();
              showUnavailableAction('Find person in logs');
            }}
          >
            <ListItemIcon>
              <Search fontSize="small" sx={{ color: logsDisabled || showDeleted ? undefined : primary }} />
            </ListItemIcon>
            <ListItemText primary="Find person in logs" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              setProfileSwitchDialog({ open: true, person });
            }}
          >
            <ListItemIcon>
              <SwapHoriz fontSize="small" sx={{ color: showDeleted ? undefined : primary }} />
            </ListItemIcon>
            <ListItemText primary="Switch profile" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              showUnavailableAction('Add validity tenure');
            }}
          >
            <ListItemIcon>
              <CalendarMonth fontSize="small" sx={{ color: showDeleted ? undefined : '#5F7EBC' }} />
            </ListItemIcon>
            <ListItemText primary="Add validity tenure" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              showUnavailableAction('Restricted cameras');
            }}
          >
            <ListItemIcon>
              <Videocam fontSize="small" sx={{ color: showDeleted ? undefined : primary }} />
            </ListItemIcon>
            <ListItemText primary="Restricted cameras" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              setSimilarityThresholdDialog({ open: true, person });
            }}
          >
            <ListItemIcon>
              <Tune fontSize="small" sx={{ color: showDeleted ? undefined : primary }} />
            </ListItemIcon>
            <ListItemText primary="Set similarity threshold" />
          </MenuItem>
          <Divider />
          {!showDeleted ? (
            <MenuItem
              onClick={() => {
                closeEnrolledManageMenu();
                handleDeleteAllFaces(person);
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <Delete fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText primary="Deactivate all faces" primaryTypographyProps={{ sx: { color: 'error.main' } }} />
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => {
                closeEnrolledManageMenu();
                handleRestoreAllFaces(person);
              }}
              sx={{ color: 'success.main' }}
            >
              <ListItemIcon>
                <Restore fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText primary="Activate all faces" primaryTypographyProps={{ sx: { color: 'success.main' } }} />
            </MenuItem>
          )}
        </Menu>
      </Box>
    );
  };

  return (
    <Box>
      <Container maxWidth="lg">
        <PageHeader
          title="Intrusion Profile Management"
          subtitle="Manage intrusion profiles - view and manage faces marked as intrusion"
        />

        {/* Filters Section - Above Card */}
        <Box sx={pageFiltersToolbarSx}>
          <Stack {...pageFiltersStackSx}>
            <Box sx={pageFiltersControlsSx}>
              <Autocomplete
                sx={{ minWidth: { xs: '100%', sm: 200 }, width: { xs: '100%', sm: 'auto' } }}
                freeSolo
                size="small"
                options={[]}
                value={queryInput || null}
                inputValue={queryInput || ''}
                onInputChange={(event, newInputValue) => {
                  setQueryInput((newInputValue || '').trimStart());
                }}
                onChange={(event, newValue) => {
                  const queryValue = typeof newValue === 'string' ? newValue : (newValue ? newValue : '');
                  applyQueryFilterNow(queryValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by Name"
                    variant="outlined"
                    size="small"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        applyQueryFilterNow(e.currentTarget.value || '');
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {/* <Search sx={{ color: 'action.active', mr: 1, my: 0.5 }} /> */}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              
              <FormControl
                sx={{
                  minWidth: { xs: '100%', sm: 150 },
                  flex: { xs: 1, sm: 'none' }
                }}
                size="small"
              >
                <InputLabel>Account</InputLabel>
                <Select
                  value={accountFilter}
                  label="Account"
                  onChange={(e) => {
                    setAccountFilter(e.target.value);
                    setPagination({ ...pagination, current_page: 1 });
                  }}
                >
                  <SelectMenuItem value="">All Accounts</SelectMenuItem>
                  {accounts.map((account) => (
                    <SelectMenuItem key={account.company_id} value={account.company_id}>
                      {account.company_name}
                    </SelectMenuItem>
                  ))}
                </Select>
              </FormControl>
              <ClearAllFiltersButton
                onClear={handleClearAllFilters}
                disabled={!hasActiveFilters}
              />
            </Box>

            <Box sx={pageFiltersActionsSx}>
              <ToggleButtonGroup
                value={showDeleted ? 'deleted' : 'active'}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setShowDeleted(newValue === 'deleted');
                    setPagination({ ...pagination, current_page: 1 });
                  }
                }}
                size="small"
                sx={{
                  height: 36,
                  '& .MuiToggleButton-root': {
                    minHeight: 36,
                    paddingTop: 0,
                    paddingBottom: 0,
                    textTransform: 'none',
                    fontSize: '0.875rem'
                  },
                }}
              >
                <ToggleButton value="active">
                  <Visibility sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                  Active
                </ToggleButton>
                <ToggleButton value="deleted">
                  <Delete sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                  Deactive
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadPersons}
                disabled={loading}
                size="small"
                sx={{
                  textTransform: 'none',
                  height: 36,
                  minWidth: 'auto',
                  px: 1.5,
                  fontSize: '0.875rem'
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={handleCreateProfile}
                size="small"
                sx={{
                  backgroundColor: primary,
                  '&:hover': { backgroundColor: '#2d3a5f' },
                  textTransform: 'none',
                  height: 36,
                  minWidth: 'auto',
                  px: 1.5,
                  fontSize: '0.875rem'
                }}
              >
                Register Intrusion Profile
              </Button>
            </Box>
          </Stack>
        </Box>

      <Card>
        <CardContent>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            visiblePersons.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {query || accountFilter ? 'No intrusion profiles found matching filters' : 'No intrusion profiles found'}
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {visiblePersons.map((person) => {
                  const matchingFaces = getMatchingFaces(person);

                  return (
                    <Card key={person.person_id} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 2 }}>
                        <Stack spacing={1.25}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {person.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Account: {person.company_name || '-'}
                            </Typography>
                          </Box>

                          <Divider />
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Updated By
                            </Typography>
                            <Box sx={{ textAlign: 'right', minWidth: 0 }}>
                              <Typography variant="body2">{resolveUpdatedByName(person)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {resolveUpdatedAt(person)}
                              </Typography>
                            </Box>
                          </Box>
                          <Divider />

                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Angles
                            </Typography>
                            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
                              {matchingFaces.map((face) => (
                                <Chip
                                  key={face.face_id}
                                  label={formatAngleLabel(face.face_angle, face.metadata || {})}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Stack>
                          </Box>

                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Voice
                            </Typography>
                            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
                              {person.checkin_voice_path && person.checkin_voice_path !== '' && (
                                <Chip 
                                  size="small" 
                                  color="primary" 
                                  variant="outlined"
                                  onClick={() => playVoice(person.person_id, 'checkin')}
                                  disabled={!!playingVoice}
                                  sx={{ 
                                    cursor: playingVoice ? 'not-allowed' : 'pointer', 
                                    '&:hover': { backgroundColor: playingVoice ? undefined : 'rgba(25, 118, 210, 0.08)' },
                                    opacity: playingVoice ? 0.6 : 1
                                  }}
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Login sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                                      <span>IN</span>
                                      <VolumeUp sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                                    </Box>
                                  }
                                />
                              )}
                              {person.checkout_voice_path && person.checkout_voice_path !== '' && (
                                <Chip 
                                  size="small" 
                                  color="secondary" 
                                  variant="outlined"
                                  onClick={() => playVoice(person.person_id, 'checkout')}
                                  disabled={!!playingVoice}
                                  sx={{ 
                                    cursor: playingVoice ? 'not-allowed' : 'pointer', 
                                    '&:hover': { backgroundColor: playingVoice ? undefined : 'rgba(156, 39, 176, 0.08)' },
                                    opacity: playingVoice ? 0.6 : 1
                                  }}
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Logout sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                                      <span>OUT</span>
                                      <VolumeUp sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                                    </Box>
                                  }
                                />
                              )}
                              {((!person.checkin_voice_path || person.checkin_voice_path === '') || 
                                (!person.checkout_voice_path || person.checkout_voice_path === '')) && (
                                <Chip 
                                  label="Add Voice" 
                                  size="small" 
                                  variant="outlined"
                                  onClick={() => openVoiceModal(person.person_id, person.name, person.person_id)}
                                  sx={{ 
                                    cursor: 'pointer',
                                    borderColor: primary,
                                    color: primary,
                                    '&:hover': { backgroundColor: 'rgba(64, 81, 138, 0.08)' }
                                  }}
                                />
                              )}
                              {person.checkin_voice_path && person.checkin_voice_path !== '' && 
                               person.checkout_voice_path && person.checkout_voice_path !== '' && (
                                <Chip 
                                  label="Update" 
                                  size="small" 
                                  variant="outlined"
                                  onClick={() => openVoiceModal(person.person_id, person.name, person.person_id, 'update')}
                                  sx={{ 
                                    cursor: 'pointer',
                                    borderColor: primary,
                                    color: primary,
                                    '&:hover': { backgroundColor: 'rgba(64, 81, 138, 0.08)' }
                                  }}
                                />
                              )}
                            </Stack>
                          </Box>

                          <Divider />

                          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                            <Tooltip title="Set similarity threshold">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => setSimilarityThresholdDialog({ open: true, person })}
                                  disabled={showDeleted}
                                  sx={{ color: primary }}
                                >
                                  <Tune fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Switch profile">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => setProfileSwitchDialog({ open: true, person })}
                                  disabled={showDeleted}
                                  sx={{ color: primary }}
                                >
                                  <SwapHoriz fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {!showDeleted ? (
                              <Tooltip title="Delete All Faces">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteAllFaces(person)}
                                  sx={{ color: '#d32f2f' }}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Activate All Faces">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRestoreAllFaces(person)}
                                  sx={{ color: '#2e7d32' }}
                                >
                                  <Restore />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={rowExpanded[person.person_id] ? 'Collapse' : 'Expand'}>
                              <IconButton
                                size="small"
                                onClick={() => toggleRowExpanded(person.person_id)}
                                color="primary"
                              >
                                {rowExpanded[person.person_id] ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </Tooltip>
                          </Stack>

                          {rowExpanded[person.person_id] ? (
                            <Stack spacing={1}>
                              {matchingFaces.map((face) => {
                                const faceData = {
                                  face_id: face.face_id,
                                  face_angle: face.face_angle,
                                  pose_quality: face.pose_quality || 0,
                                  lighting_quality: face.lighting_quality || 0,
                                  created_at: face.created_at,
                                  last_updated: face.last_updated,
                                  deleted_at: face.deleted_at,
                                  metadata: face.metadata || {},
                                  name: person.name,
                                  person_id: person.person_id
                                };

                                return (
                                  <Card key={face.face_id} variant="outlined" sx={{ borderRadius: 2, bgcolor: 'grey.50' }}>
                                    <CardContent sx={{ p: 1.5 }}>
                                      <Stack spacing={1}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                          <Chip
                                            label={formatAngleLabel(faceData.face_angle, faceData.metadata)}
                                            size="small"
                                            sx={{ fontWeight: 700 }}
                                          />
                                          <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Tooltip title="View image">
                                              <IconButton size="small" onClick={() => handleViewFaceImage(faceData)} color="primary">
                                                <Visibility />
                                              </IconButton>
                                            </Tooltip>
                                            {!showDeleted && !faceData.deleted_at ? (
                                              <Tooltip title="Edit image">
                                                <IconButton size="small" onClick={() => handleOpenEdit(faceData)} color="primary">
                                                  <Edit />
                                                </IconButton>
                                              </Tooltip>
                                            ) : null}
                                            {!showDeleted && !faceData.deleted_at ? (
                                              <Tooltip title="Delete">
                                                <IconButton size="small" onClick={() => handleSoftDeleteFace(faceData)} sx={{ color: '#d32f2f' }}>
                                                  <Delete />
                                                </IconButton>
                                              </Tooltip>
                                            ) : showDeleted && faceData.deleted_at ? (
                                              <Tooltip title="Activate">
                                                <IconButton size="small" onClick={() => handleRestoreFace(faceData)} sx={{ color: '#2e7d32' }}>
                                                  <Restore />
                                                </IconButton>
                                              </Tooltip>
                                            ) : null}
                                          </Stack>
                                        </Box>

                                        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                          <Chip 
                                            label={`Pose ${(faceData.pose_quality * 100).toFixed(1)}%`} 
                                            size="small" 
                                            sx={{ 
                                              borderRadius: '6px', 
                                              border: '1px solid', 
                                              borderColor: getQualityColor(faceData.pose_quality), 
                                              backgroundColor: 'white' 
                                            }} 
                                          />
                                          <Chip 
                                            label={`Light ${(faceData.lighting_quality * 100).toFixed(1)}%`} 
                                            size="small" 
                                            sx={{ 
                                              borderRadius: '6px', 
                                              border: '1px solid', 
                                              borderColor: getQualityColor(faceData.lighting_quality), 
                                              backgroundColor: 'white' 
                                            }} 
                                          />
                                        </Stack>

                                        <Typography variant="caption" color="text.secondary">
                                          Created: {formatDate(faceData.created_at)} · Updated: {formatDate(faceData.last_updated)}
                                        </Typography>
                                      </Stack>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </Stack>
                          ) : null}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <TableContainer component={Paper} variant="outlined">
                <Table sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Updated By</TableCell>
                    <TableCell>Angles</TableCell>
                    <TableCell>Voice</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiblePersons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {query || accountFilter ? 'No intrusion profiles found matching filters' : 'No intrusion profiles found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visiblePersons.map((person) => (
                      <React.Fragment key={person.person_id}>
                        <TableRow hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {person.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{person.company_name || '-'}</TableCell>
                          <TableCell>{renderUpdatedByCell(person)}</TableCell>
                          <TableCell>{renderEnrolledAngles(person)}</TableCell>
                          <TableCell>{renderEnrolledVoice(person)}</TableCell>
                          <TableCell>{renderEnrolledActions(person)}</TableCell>
                        </TableRow>
                        {rowExpanded[person.person_id] && getMatchingFaces(person).length > 0 && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ p: 0 }}>
                              <Box sx={{ backgroundColor: 'rgba(0,0,0,0.02)', px: 2, py: 1 }}>
                                <Table size="small" sx={{ m: 0 }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Angle</TableCell>
                                      <TableCell>Pose</TableCell>
                                      <TableCell>Lighting</TableCell>
                                      <TableCell>Created</TableCell>
                                      <TableCell>Updated</TableCell>
                                      <TableCell>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {getMatchingFaces(person).map((face) => {
                                      const faceData = {
                                        face_id: face.face_id,
                                        face_angle: face.face_angle,
                                        pose_quality: face.pose_quality || 0,
                                        lighting_quality: face.lighting_quality || 0,
                                        created_at: face.created_at,
                                        last_updated: face.last_updated,
                                        deleted_at: face.deleted_at,
                                        metadata: face.metadata || {},
                                        name: person.name,
                                        person_id: person.person_id
                                      };
                                      return (
                                        <TableRow key={face.face_id}>
                                          <TableCell>
                                            <Chip label={formatAngleLabel(faceData.face_angle, faceData.metadata)} size="small" />
                                          </TableCell>
                                          <TableCell>
                                            <Chip 
                                              label={`${(faceData.pose_quality * 100).toFixed(1)}%`} 
                                              size="small" 
                                              sx={{ 
                                                borderRadius: '6px', 
                                                border: '1px solid', 
                                                borderColor: getQualityColor(faceData.pose_quality), 
                                                backgroundColor: 'white' 
                                              }} 
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Chip 
                                              label={`${(faceData.lighting_quality * 100).toFixed(1)}%`} 
                                              size="small" 
                                              sx={{ 
                                                borderRadius: '6px', 
                                                border: '1px solid', 
                                                borderColor: getQualityColor(faceData.lighting_quality), 
                                                backgroundColor: 'white' 
                                              }} 
                                            />
                                          </TableCell>
                                          <TableCell>{formatDate(faceData.created_at)}</TableCell>
                                          <TableCell>{formatDate(faceData.last_updated)}</TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              <Button title="View image" size="small" startIcon={<img src={"/assets/icons/view-image-icon.png"} alt="" style={{ width: 20, height: 20 }} />} onClick={() => handleViewFaceImage(faceData)} />
                                              {!showDeleted && !faceData.deleted_at && (
                                                <Button title="Edit image" size="small" onClick={() => handleOpenEdit(faceData)} startIcon={<img src={"/assets/icons/edit-image-icon.png"} alt="" style={{ width: 20, height: 20 }} />} disableRipple sx={{ color: 'text.disabled' }} />
                                              )}
                                              {!showDeleted && !faceData.deleted_at ? (
                                                <Button title="Deactivate" size="small" startIcon={<Delete />} style={{ color: '#5F7EBC' }} onClick={() => handleSoftDeleteFace(faceData)} />
                                              ) : showDeleted && faceData.deleted_at ? (
                                                <Button title="Activate" size="small" startIcon={<Restore />} style={{ color: '#5F7EBC' }} onClick={() => handleRestoreFace(faceData)} />
                                              ) : null}
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
              </TableContainer>
            </Box>
          )}

          {pagination.total > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                mt: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Rows per page:
                </Typography>
                <FormControl size="small">
                  <Select
                    value={pagination.limit}
                    onChange={handleLimitChange}
                    sx={{ minWidth: 80 }}
                  >
                    <SelectMenuItem value={10}>10</SelectMenuItem>
                    <SelectMenuItem value={20}>20</SelectMenuItem>
                    <SelectMenuItem value={50}>50</SelectMenuItem>
                    <SelectMenuItem value={100}>100</SelectMenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min(pagination.offset + 1, pagination.total)} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
                </Typography>
              </Box>
              <Pagination
                count={pagination.total_pages}
                page={pagination.current_page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                size="small"
                sx={{
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                  '& .MuiPagination-ul': {
                    pl: 0,
                    ml: 0,
                  },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Face Image Dialog */}
      <Dialog
        open={imageDialog.open}
        onClose={() => {
          if (imageDialog.imageUrl) {
            URL.revokeObjectURL(imageDialog.imageUrl);
          }
          setImageDialog({ open: false, imageUrl: null, faceName: '' });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Face Image - {imageDialog.faceName}</DialogTitle>
        <DialogContent>
          {imageDialog.imageUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <img
                src={imageDialog.imageUrl}
                alt={`Face for ${imageDialog.faceName}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (imageDialog.imageUrl) {
                URL.revokeObjectURL(imageDialog.imageUrl);
              }
              setImageDialog({ open: false, imageUrl: null, faceName: '' });
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <PersonProfileSwitchDialog
        open={profileSwitchDialog.open}
        sourceKind="intrusion"
        person={profileSwitchDialog.person}
        onClose={() => setProfileSwitchDialog({ open: false, person: null })}
        onSuccess={(message) => {
          showSnackbar(message, 'success');
          loadPersons();
        }}
        onError={(message, severity = 'error') => showSnackbar(message, severity)}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              confirmDialog.onConfirm();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Image Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Intrusion Profile Image</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a new photo or capture using your camera. Keep the face centered and well lit.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Preview */}
            {!isCameraOn && previewUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 8 }}
                />
              </Box>
            )}

            {/* Upload */}
            <Button variant="outlined" component="label">
              Choose Image
              <input hidden accept="image/*" type="file" onChange={onFileChange} />
            </Button>

            <Divider>or</Divider>

            {/* Camera */}
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button
                  variant={isCameraOn ? 'outlined' : 'contained'}
                  onClick={toggleCamera}
                  sx={{
                    borderColor: primary,
                    backgroundColor: isCameraOn ? 'transparent' : primary,
                    color: isCameraOn ? primary : 'white',
                  }}
                >
                  {isCameraOn ? 'Stop Camera' : 'Start Camera'}
                </Button>
                <Button variant="outlined" onClick={capturePhoto} disabled={!isCameraOn}>
                  Capture
                </Button>
              </Box>
              {isCameraOn && (
                <Box
                  sx={{
                    mt: 2,
                    backgroundColor: '#000',
                    border: '3px solid #1976d2',
                    borderRadius: 3,
                    overflow: 'hidden',
                    width: '100%',
                    maxWidth: 400,
                    mx: 'auto',
                  }}
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button onClick={submitEdit} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Voice Upload Modal */}
      <Dialog
        open={voiceModalOpen}
        onClose={closeVoiceModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px', overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: primary,
          color: 'white',
          py: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VolumeUp />
            <Typography variant="h6" component="span">
              {voiceModalEmployee?.mode === 'update' ? 'Update' : 'Add'} Voice for {voiceModalEmployee?.employeeName || 'Employee'}
            </Typography>
          </Box>
          <IconButton onClick={closeVoiceModal} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, pb: 2, mt: 2.0 }}>
          {/* IN Voice Section */}
          <Box sx={{ 
            mb: 3, 
            p: 2.5, 
            border: '1px solid',
            borderColor: '#e0e0e0',
            borderRadius: '12px',
            backgroundColor: '#fff'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Login sx={{ fontSize: 20, color: '#555' }} />
              IN Voice (Check-in)
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Upload Button */}
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ textTransform: 'none' }}
              >
                Upload WAV
                <input
                  type="file"
                  accept=".wav,audio/wav"
                  hidden
                  onChange={(e) => handleFileUpload(e, 'checkin')}
                />
              </Button>

              <Button
                variant="outlined"
                startIcon={<SmartToy />}
                onClick={() => openAIVoiceDialog('checkin')}
                sx={{ textTransform: 'none' }}
              >
                AI Generate Voice
              </Button>
              
              {/* Record Button */}
              {!isRecordingCheckin ? (
                <Button
                  variant="outlined"
                  startIcon={<Mic />}
                  onClick={() => startRecording('checkin')}
                  sx={{ 
                    textTransform: 'none',
                    borderColor: '#d32f2f',
                    color: '#d32f2f',
                    '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                  }}
                >
                  Record
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Stop />}
                  onClick={() => stopRecording('checkin')}
                  sx={{ 
                    textTransform: 'none',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
                      '70%': { boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: 'white',
                      animation: 'blink 0.8s ease-in-out infinite',
                      '@keyframes blink': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.3 },
                      }
                    }} />
                    Recording...
                  </Box>
                </Button>
              )}
            </Box>
            
            {/* Recording Animation */}
            {isRecordingCheckin && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 24 }}>
                  {[...Array(5)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 4,
                        backgroundColor: '#d32f2f',
                        borderRadius: 1,
                        animation: `wave 0.8s ease-in-out infinite`,
                        animationDelay: `${i * 0.1}s`,
                        '@keyframes wave': {
                          '0%, 100%': { height: 8 },
                          '50%': { height: 24 },
                        }
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="error">
                  Speak now...
                </Typography>
              </Box>
            )}

            {/* Audio Player */}
            {checkinAudioUrl && !isRecordingCheckin && (
              <Box sx={{ mt: 2.5 }}>
                <AudioPlayer
                  src={checkinAudioUrl}
                  showJumpControls={false}
                  showDownloadProgress={false}
                  showFilledProgress={true}
                  customAdditionalControls={[]}
                  customVolumeControls={[]}
                  layout="horizontal-reverse"
                  style={{ 
                    borderRadius: '8px',
                    boxShadow: 'none',
                    backgroundColor: '#f5f5f5'
                  }}
                />
              </Box>
            )}
          </Box>

          {/* OUT Voice Section */}
          <Box sx={{ 
            p: 2.5, 
            border: '1px solid',
            borderColor: '#e0e0e0',
            borderRadius: '12px',
            backgroundColor: '#fff'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Logout sx={{ fontSize: 20, color: '#555' }} />
              OUT Voice (Check-out)
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Upload Button */}
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ textTransform: 'none' }}
              >
                Upload WAV
                <input
                  type="file"
                  accept=".wav,audio/wav"
                  hidden
                  onChange={(e) => handleFileUpload(e, 'checkout')}
                />
              </Button>

              <Button
                variant="outlined"
                startIcon={<SmartToy />}
                onClick={() => openAIVoiceDialog('checkout')}
                sx={{ textTransform: 'none' }}
              >
                AI Generate Voice
              </Button>
              
              {/* Record Button */}
              {!isRecordingCheckout ? (
                <Button
                  variant="outlined"
                  startIcon={<Mic />}
                  onClick={() => startRecording('checkout')}
                  sx={{ 
                    textTransform: 'none',
                    borderColor: '#d32f2f',
                    color: '#d32f2f',
                    '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                  }}
                >
                  Record
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Stop />}
                  onClick={() => stopRecording('checkout')}
                  sx={{ 
                    textTransform: 'none',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: 'white',
                      animation: 'blink 0.8s ease-in-out infinite',
                    }} />
                    Recording...
                  </Box>
                </Button>
              )}
            </Box>
            
            {/* Recording Animation */}
            {isRecordingCheckout && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 24 }}>
                  {[...Array(5)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 4,
                        backgroundColor: '#d32f2f',
                        borderRadius: 1,
                        animation: `wave 0.8s ease-in-out infinite`,
                        animationDelay: `${i * 0.1}s`,
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="error">
                  Speak now...
                </Typography>
              </Box>
            )}

            {/* Audio Player */}
            {checkoutAudioUrl && !isRecordingCheckout && (
              <Box sx={{ mt: 2.5 }}>
                <AudioPlayer
                  src={checkoutAudioUrl}
                  showJumpControls={false}
                  showDownloadProgress={false}
                  showFilledProgress={true}
                  customAdditionalControls={[]}
                  customVolumeControls={[]}
                  layout="horizontal-reverse"
                  style={{ 
                    borderRadius: '8px',
                    boxShadow: 'none',
                    backgroundColor: '#f5f5f5'
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={closeVoiceModal}
            disabled={voiceUploading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveVoices}
            disabled={voiceUploading || (!checkinVoiceBlob && !checkoutVoiceBlob)}
            startIcon={voiceUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
            sx={{ backgroundColor: primary, '&:hover': { backgroundColor: '#2e3d66' } }}
          >
            {voiceUploading ? 'Uploading...' : 'Save Voices'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Voice Dialog */}
      <Dialog
        open={aiVoiceDialogOpen}
        onClose={closeAIVoiceDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          AI Generate {aiVoiceType === 'checkin' ? 'IN' : 'OUT'} Voice
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Key Type: Cartesia API Key (voice_generation)
          </Typography>
          <Autocomplete
            freeSolo
            fullWidth
            options={aiVoiceSavedKeys}
            value={aiVoiceSavedKeys.find((key) => key.credential_id === aiVoiceSelectedKeyId) || null}
            inputValue={aiVoiceApiKeyInput}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') {
                setAiVoiceSelectedKeyId('');
                setAiVoiceApiKeyInput(newValue);
                return;
              }
              if (newValue?.credential_id) {
                setAiVoiceSelectedKeyId(newValue.credential_id);
                setAiVoiceApiKeyInput(newValue.credential_hint || '');
                return;
              }
              setAiVoiceSelectedKeyId('');
              setAiVoiceApiKeyInput('');
            }}
            onInputChange={(_, newInputValue, reason) => {
              setAiVoiceApiKeyInput(newInputValue);
              if (reason === 'input') {
                setAiVoiceSelectedKeyId('');
              }
            }}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.credential_hint || option.credential_id)}
            loading={aiVoiceLoadingKeys}
            disabled={aiVoiceGenerating}
            sx={{ mb: 2 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Saved API Key"
                type={aiVoiceSelectedKeyId ? 'text' : 'password'}
                placeholder="Select saved key or enter new API key"
                autoComplete="off"
                inputProps={{ ...params.inputProps, autoComplete: 'off' }}
              />
            )}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Voice Text"
            value={aiVoiceText}
            onChange={(e) => setAiVoiceText(e.target.value)}
            disabled={aiVoiceGenerating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAIVoiceDialog} disabled={aiVoiceGenerating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateAIVoice}
            disabled={aiVoiceGenerating || (!aiVoiceSelectedKeyId && !aiVoiceApiKeyInput.trim())}
            startIcon={aiVoiceGenerating ? <CircularProgress size={16} color="inherit" /> : <SmartToy />}
            sx={{ backgroundColor: primary, '&:hover': { backgroundColor: '#2e3d66' } }}
          >
            {aiVoiceGenerating ? 'Generating...' : 'Generate Voice'}
          </Button>
        </DialogActions>
      </Dialog>

      <SimilarityThresholdDialog
        open={similarityThresholdDialog.open}
        person={similarityThresholdDialog.person}
        onClose={() => setSimilarityThresholdDialog({ open: false, person: null })}
        onSaved={handleSimilarityThresholdSaved}
        showSnackbar={showSnackbar}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

const primary = '#40518A';

export default IntrusionProfiles;
